import { Controller } from "@hotwired/stimulus";

// CmdPaletteController drives the command palette modal. It owns the
// search input + results list; the keyboard controller is responsible
// for showing/hiding the modal element itself.
//
// On `palette:open` (dispatched by the keyboard controller when
// Cmd/Ctrl+K fires), the input is focused and any prior query state
// is reset so the user always starts with a clean prompt.
//
// Results are rendered into two sections (Explorations, Findings).
// Arrow keys navigate across both sections in document order; Enter
// visits the active row via Turbo; Escape closes the modal.
// Nav items show up in a "Pages" section at the top of the palette,
// either filtered by the current query or as the default landing list
// when the palette opens empty. Items flagged `admin` are skipped for
// non-admin sessions via the isAdmin Stimulus value.
// Static destinations offered by the palette, filtered by `admin`.
//
// ⚠️ ADDING A PAGE? Add it here too. A new route + nav entry in base.html is
// only two thirds of the job — the ⌘/Ctrl+K palette has no knowledge of the
// router, so an omitted page is simply unreachable by keyboard. Keep the
// order roughly matching the sidebar so the palette reads the same way.
const NAV_ITEMS = [
  { label: "Vulnerabilities",     href: "/",                     admin: false },
  { label: "Explorations",        href: "/explorations",         admin: false },
  { label: "Pull requests",       href: "/pull-requests",        admin: false },
  { label: "New exploration",     href: "/explorations/new",     admin: false },
  { label: "Workers",             href: "/workers",              admin: false },
  { label: "Runs",                href: "/runs",                 admin: false },
  { label: "Recon",               href: "/recon",                admin: false },
  { label: "Memory",              href: "/memory",               admin: false },
  { label: "Scan history",        href: "/repo-scan",            admin: false },
  { label: "Preferences",         href: "/preferences",          admin: false },
  { label: "Admin · Users",       href: "/admin/users",          admin: true  },
  { label: "Admin · Settings",    href: "/settings",             admin: true  },
];

export default class CmdPaletteController extends Controller {
  static targets = ["input", "results", "empty"];
  static values  = { url: String, isAdmin: Boolean };

  connect() {
    this.activeIndex = -1;
    this.fetchSeq = 0;
    this.debounce = null;
    this.onOpen = () => this.handleOpen();
    this.element.addEventListener("palette:open", this.onOpen);
  }

  navItems() {
    return NAV_ITEMS.filter((n) => this.isAdminValue || !n.admin);
  }

  // matchNav scores each nav item against q (case-insensitive substring)
  // and returns the matches in score order. Score breaks ties so e.g.
  // "ex" prefers "Explorations" (label starts with "ex") over
  // "New exploration" (substring match further into the label).
  matchNav(q) {
    if (!q) return this.navItems();
    const lq = q.toLowerCase();
    const scored = [];
    for (const n of this.navItems()) {
      const ll = n.label.toLowerCase();
      let score;
      if (ll.startsWith(lq))      score = 0;
      else if (ll.includes(lq))   score = 1;
      else continue;
      scored.push({ n, score });
    }
    scored.sort((a, b) => a.score - b.score);
    return scored.map((x) => x.n);
  }

  disconnect() {
    this.element.removeEventListener("palette:open", this.onOpen);
    if (this.debounce) clearTimeout(this.debounce);
  }

  handleOpen() {
    this.inputTarget.value = "";
    this.activeIndex = -1;
    // Show nav shortcuts as the default landing state — discoverability
    // beats a "Type to search…" placeholder, especially for new users.
    this.renderNavOnly("");
    // focus() on a still-display:none element silently no-ops, so we
    // defer past the next paint to be sure the modal is laid out.
    // requestAnimationFrame is more reliable than setTimeout(0) here:
    // the timer would fire before the browser's style/layout pass had
    // applied the `hidden` removal, leaving the input invisible to
    // focus(). A double-rAF guarantees we're past the paint even when
    // the browser batches the style flush.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.inputTarget.focus());
    });
  }

  renderNavOnly(q) {
    const navs = this.matchNav(q);
    if (navs.length === 0) {
      this.showEmpty(q === "" ? "No pages available." : `No pages match "${q}".`);
      return;
    }
    const frag = document.createDocumentFragment();
    frag.appendChild(sectionHeader("Pages"));
    for (const n of navs) frag.appendChild(navRow(n, q));
    this.resultsTarget.replaceChildren(frag);
    this.emptyTarget.classList.add("hidden");
    this.bindClickOnce();
    if (this.rows().length > 0) this.setActive(0);
  }

  close() {
    this.element.classList.add("hidden");
  }

  onInput() {
    const q = this.inputTarget.value.trim();
    if (this.debounce) clearTimeout(this.debounce);
    // Below the search-trigger threshold we still want nav matches —
    // typing "p" should instantly suggest "Preferences" without a
    // round-trip to /api/search.
    if (q.length < 2) {
      this.renderNavOnly(q);
      return;
    }
    this.debounce = setTimeout(() => this.fetch(q), 120);
  }

  onKeyDown(e) {
    const rows = this.rows();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (rows.length === 0) return;
      this.setActive((this.activeIndex + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (rows.length === 0) return;
      this.setActive((this.activeIndex - 1 + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      if (this.activeIndex >= 0 && rows[this.activeIndex]) {
        e.preventDefault();
        const href = rows[this.activeIndex].dataset.href;
        if (href) this.visit(href);
      }
    }
    // Escape is handled by the global keyboard controller (closeAllModals).
  }

  async fetch(q) {
    const seq = ++this.fetchSeq;
    let data;
    try {
      const res = await fetch(`${this.urlValue}?q=${encodeURIComponent(q)}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(res.status);
      data = await res.json();
    } catch (err) {
      this.renderError();
      return;
    }
    if (seq !== this.fetchSeq) return; // out-of-order response
    this.render(data, q);
  }

  render(data, q) {
    const exps  = data.explorations || [];
    const vulns = data.vulns || [];
    const navs  = this.matchNav(q);

    if (exps.length === 0 && vulns.length === 0 && navs.length === 0) {
      this.showEmpty(`No matches for "${q}".`);
      return;
    }

    const frag = document.createDocumentFragment();
    if (navs.length > 0) {
      frag.appendChild(sectionHeader("Pages"));
      for (const n of navs) frag.appendChild(navRow(n, q));
    }
    if (exps.length > 0) {
      frag.appendChild(sectionHeader("Explorations"));
      for (const e of exps) frag.appendChild(explorationRow(e, q));
    }
    if (vulns.length > 0) {
      frag.appendChild(sectionHeader("Findings"));
      for (const v of vulns) frag.appendChild(vulnRow(v, q));
    }
    this.resultsTarget.replaceChildren(frag);
    this.emptyTarget.classList.add("hidden");
    this.activeIndex = -1;

    this.bindClickOnce();

    // Pre-select the first row for keyboard-first users.
    if (this.rows().length > 0) this.setActive(0);
  }

  // bindClickOnce attaches a single mousedown handler on the results
  // container; row dispatch goes through closest("[data-href]"). The
  // bind is idempotent across renders, so a fresh palette-open doesn't
  // accumulate listeners.
  bindClickOnce() {
    if (this.clickBound) return;
    this.resultsTarget.addEventListener("mousedown", (e) => {
      const row = e.target.closest("[data-href]");
      if (!row) return;
      e.preventDefault();
      this.visit(row.dataset.href);
    });
    this.clickBound = true;
  }

  renderError() {
    this.showEmpty("Search failed — try again.");
  }

  // showEmpty clears the result rows and shows the message beside them. The
  // empty element must stay a sibling of the results container — moving it
  // *inside* via replaceChildren(this.emptyTarget) deletes it from the DOM on
  // the next successful render, and every empty response after that throws on
  // the missing target and leaves the stale rows on screen.
  showEmpty(msg) {
    this.resultsTarget.replaceChildren();
    this.activeIndex = -1;
    this.emptyTarget.textContent = msg;
    this.emptyTarget.classList.remove("hidden");
  }

  visit(href) {
    this.close();
    if (window.Turbo) Turbo.visit(href); else window.location.href = href;
  }

  rows() { return Array.from(this.resultsTarget.querySelectorAll("[data-href]")); }

  setActive(i) {
    const rows = this.rows();
    rows.forEach((el, idx) => {
      el.classList.toggle("is-active", idx === i);
    });
    this.activeIndex = i;
    if (rows[i]) rows[i].scrollIntoView({ block: "nearest" });
  }
}

// navRow renders a "Pages" entry — a small arrow icon + the label
// with the query substring highlighted. Same data-href contract as
// the search-result rows so a single click handler dispatches them.
function navRow(n, q) {
  const row = document.createElement("a");
  row.dataset.href = n.href;
  row.href = n.href;
  row.className = "flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer cmdp-row";
  row.innerHTML =
    '<svg class="w-3.5 h-3.5 shrink-0 text-gray-400 dark:text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">' +
    '<path fill-rule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z" clip-rule="evenodd"/></svg>' +
    '<span class="text-gray-700 dark:text-gray-200">' + highlight(n.label, q) + "</span>";
  return row;
}

function sectionHeader(label) {
  const h = document.createElement("div");
  h.className = "px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-500";
  h.textContent = label;
  return h;
}

function explorationRow(e, q) {
  const row = document.createElement("a");
  row.dataset.href = "/explorations/" + e.id;
  row.href = row.dataset.href;
  row.className = "flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm cursor-pointer cmdp-row";
  const left = document.createElement("span");
  left.className = "flex items-center gap-2 min-w-0";
  left.appendChild(fileGlyph());
  const path = document.createElement("span");
  path.className = "font-mono text-gray-700 dark:text-gray-200 truncate";
  path.innerHTML = highlight(e.path, q);
  left.appendChild(path);
  row.appendChild(left);
  if (e.findings_count > 0) {
    const c = document.createElement("span");
    c.className = "shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400";
    c.textContent = e.findings_count + " finding" + (e.findings_count === 1 ? "" : "s");
    row.appendChild(c);
  }
  return row;
}

function vulnRow(v, q) {
  const row = document.createElement("a");
  row.dataset.href = "/vulns/" + v.id;
  row.href = row.dataset.href;
  row.className = "flex items-start justify-between gap-3 px-3 py-2 rounded-md cursor-pointer cmdp-row";
  const left = document.createElement("div");
  left.className = "flex-1 min-w-0";
  const title = document.createElement("div");
  title.className = "text-sm text-gray-900 dark:text-gray-100 truncate";
  title.innerHTML = highlight(v.title, q);
  left.appendChild(title);
  const sub = document.createElement("div");
  sub.className = "text-xs font-mono text-gray-500 dark:text-gray-500 truncate mt-0.5";
  sub.textContent = v.path;
  left.appendChild(sub);
  row.appendChild(left);
  if (v.severity) {
    const s = document.createElement("span");
    const sev = v.severity.toLowerCase();
    const tone = sev === "critical" ? "bg-red-600 text-white"
               : sev === "high"     ? "bg-orange-500 text-white"
               : sev === "medium"   ? "bg-yellow-500 text-gray-900"
               :                      "bg-gray-500 text-white";
    s.className = "shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full self-center " + tone;
    s.textContent = v.severity;
    row.appendChild(s);
  }
  return row;
}

function fileGlyph() {
  const span = document.createElement("span");
  span.innerHTML = '<svg class="w-3.5 h-3.5 shrink-0 text-gray-400 dark:text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M4 4a2 2 0 0 1 2-2h5.293A2 2 0 0 1 12.707 2.586l2.707 2.707A2 2 0 0 1 16 6.707V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4Zm7 1V3.414L13.586 6H12a1 1 0 0 1-1-1Z" clip-rule="evenodd"/></svg>';
  return span.firstElementChild;
}

function escapeHTML(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function highlight(text, q) {
  if (!q) return escapeHTML(text);
  const lq = q.toLowerCase();
  const lower = text.toLowerCase();
  let out = "";
  let i = 0;
  while (i < text.length) {
    const idx = lower.indexOf(lq, i);
    if (idx < 0) { out += escapeHTML(text.slice(i)); break; }
    out += escapeHTML(text.slice(i, idx));
    out += '<strong class="fa-hit">' + escapeHTML(text.slice(idx, idx + lq.length)) + "</strong>";
    i = idx + lq.length;
  }
  return out;
}
